import { useEffect, useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/layout/Layout'
import { useAuth } from '../hooks/useAuth'
import { claimLicense } from '../lib/api'
import './RegistrationPage.css'

const ERROR_MESSAGES: Record<string, { titleKey: string; messageKey: string }> = {
  'missing-token': {
    titleKey: 'registration.missingTokenTitle',
    messageKey: 'registration.missingTokenMessage'
  },
  'product-mismatch': {
    titleKey: 'registration.productMismatchTitle',
    messageKey: 'registration.productMismatchMessage'
  },
  'invalid-token': {
    titleKey: 'registration.invalidTokenTitle',
    messageKey: 'registration.invalidTokenMessage'
  }
}

function RegistrationPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const claimToken = searchParams.get('claimToken')
  const errorCode = searchParams.get('error')
  const { isAuthenticated, accessToken, loading: authLoading, login } = useAuth()
  const navigate = useNavigate()
  const [claimError, setClaimError] = useState<string | null>(null)
  const [claiming, setClaiming] = useState(false)

  useEffect(() => {
    if (authLoading || !claimToken || !isAuthenticated || !accessToken) return
    setClaiming(true)
    claimLicense(claimToken, accessToken).then(result => {
      setClaiming(false)
      if (result.ok || result.status === 409) {
        // 409 = already linked to this user, still valid — go to dashboard
        void navigate('/dashboard')
      } else if (result.status === 404) {
        setClaimError(t('registration.expiredLink'))
      } else {
        setClaimError(result.error)
      }
    })
  }, [authLoading, claimToken, isAuthenticated, accessToken, navigate, t])

  // No claimToken and no error — arrived directly
  if (!claimToken && !errorCode) {
    if (isAuthenticated) {
      void navigate('/dashboard')
      return null
    }
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-error'>
            <h1>{t('registration.title')}</h1>
            <p>
              {t('registration.directArrival')} <Link to='/'>{t('registration.homePage')}</Link>.
            </p>
            <p>
              {t('registration.alreadyHaveAccount')}{' '}
              <button className='btn btn-text' style={{ display: 'inline', padding: 0 }} onClick={() => login('/dashboard')}>
                {t('dashboard.signIn')}
              </button>
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  if (errorCode) {
    const error = ERROR_MESSAGES[errorCode] ?? {
      titleKey: 'registration.error',
      messageKey: 'registration.unexpectedError'
    }

    return (
      <Layout>
        <div className='registration'>
          <div className='registration-error'>
            <h1>{t(error.titleKey)}</h1>
            <p>{t(error.messageKey)}</p>
            <p>
              {t('registration.contactSupport')}{' '}
              <a href='mailto:contact@cloudsoft.com.pl'>{t('registration.contactSupportLink')}</a>.
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  if (claimError) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-error'>
            <h1>{t('registration.error')}</h1>
            <p>{claimError}</p>
            <p>
              {t('registration.contactSupport')}{' '}
              <a href='mailto:contact@cloudsoft.com.pl'>{t('registration.contactSupportLink')}</a>.
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  // claimToken present — show sign-up prompt or claiming spinner
  if (authLoading || claiming) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-success'>
            <h1>{t('registration.completingRegistration')}</h1>
            <p>{t('registration.verifyingSubscription')}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-success'>
            <h1>{t('registration.subscriptionConfirmed')}</h1>
            <p>
              {t('registration.subscriptionThankYou')}
            </p>
            <button className='btn btn-primary' onClick={() => login(`/registration?claimToken=${claimToken}`)}>
              {t('registration.createAccountSignIn')}
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return null
}

export default RegistrationPage
